import { Add as AddIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { ClassForm } from '../../lib/components/ClassForm';
import { SetForm } from '../../lib/components/SetForm';
import { StudentTable } from '../../lib/components/StudentTable';
import { YearGroupForm } from '../../lib/components/YearGroupForm';
import { YearGroupTree } from '../../lib/components/YearGroupTree';
import { useStudentStore } from '../../stores/studentStore';
import type { Class, Set, YearGroup } from '../../types';

type TabValue = 'year-groups' | 'classes' | 'sets';
type DialogMode = 'create' | 'edit' | 'view' | null;
type DialogType = 'yearGroup' | 'class' | 'set' | null;

export const Students: React.FC = () => {
  const {
    students,
    yearGroups,
    classes,
    sets,
    addYearGroup,
    updateYearGroup,
    addClass,
    updateClass,
    addSet,
    updateSet,
  } = useStudentStore();

  const [activeTab, setActiveTab] = useState<TabValue>('year-groups');
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [dialogType, setDialogType] = useState<DialogType>(null);
  const [selectedYearGroup, setSelectedYearGroup] = useState<YearGroup | null>(null);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [selectedSet, setSelectedSet] = useState<Set | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  const handleCreateYearGroup = () => {
    setSelectedYearGroup(null);
    setDialogType('yearGroup');
    setDialogMode('create');
  };

  const handleCreateClass = () => {
    setSelectedClass(null);
    setDialogType('class');
    setDialogMode('create');
  };

  const handleCreateSet = () => {
    setSelectedSet(null);
    setDialogType('set');
    setDialogMode('create');
  };

  const handleYearGroupSubmit = (data: YearGroup) => {
    if (dialogMode === 'create') {
      addYearGroup(data);
    } else if (dialogMode === 'edit' && selectedYearGroup) {
      updateYearGroup(selectedYearGroup.id, data);
    }
    setDialogMode(null);
    setDialogType(null);
    setSelectedYearGroup(null);
  };

  const handleClassSubmit = (data: Class) => {
    if (dialogMode === 'create') {
      addClass(data);
    } else if (dialogMode === 'edit' && selectedClass) {
      updateClass(selectedClass.id, data);
    }
    setDialogMode(null);
    setDialogType(null);
    setSelectedClass(null);
  };

  const handleSetSubmit = (data: Set) => {
    if (dialogMode === 'create') {
      addSet(data);
    } else if (dialogMode === 'edit' && selectedSet) {
      updateSet(selectedSet.id, data);
    }
    setDialogMode(null);
    setDialogType(null);
    setSelectedSet(null);
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setDialogType(null);
    setSelectedYearGroup(null);
    setSelectedClass(null);
    setSelectedSet(null);
  };

  // Stats
  const totalStudents = students.length;
  const totalYearGroups = yearGroups.length;
  const totalClasses = classes.length;
  const totalSets = sets.length;

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Students & Groupings
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeTab === 'year-groups' && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateYearGroup}>
                Add Year Group
              </Button>
            )}
            {activeTab === 'classes' && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateClass}>
                Add Class
              </Button>
            )}
            {activeTab === 'sets' && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateSet}>
                Add Set
              </Button>
            )}
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Total Students
              </Typography>
              <Typography variant="h3">{totalStudents}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Year Groups
              </Typography>
              <Typography variant="h3">{totalYearGroups}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Classes
              </Typography>
              <Typography variant="h3">{totalClasses}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Ability Sets
              </Typography>
              <Typography variant="h3">{totalSets}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab label="Year Groups & Classes" value="year-groups" />
            <Tab label="Classes" value="classes" />
            <Tab label="Ability Sets" value="sets" />
          </Tabs>
        </Paper>

        {/* Year Groups Tab */}
        {activeTab === 'year-groups' && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <YearGroupTree
                yearGroups={yearGroups}
                classes={classes}
                students={students}
                onYearGroupSelect={setSelectedYearGroup}
                onClassSelect={setSelectedClass}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  All Students
                </Typography>
                <StudentTable students={students} />
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Classes Tab */}
        {activeTab === 'classes' && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Classes Overview
            </Typography>
            <Grid container spacing={2}>
              {classes.map((classData) => (
                <Grid item xs={12} sm={6} md={4} key={classData.id}>
                  <Paper sx={{ p: 2, cursor: 'pointer' }} onClick={() => setSelectedClass(classData)}>
                    <Typography variant="h6">{classData.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {yearGroups.find((yg) => yg.id === classData.yearGroupId)?.name}
                    </Typography>
                    <Typography variant="body2">
                      {classData.currentSize}/{classData.capacity} students
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Sets Tab */}
        {activeTab === 'sets' && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ability Sets
            </Typography>
            <Grid container spacing={2}>
              {sets.map((set) => (
                <Grid item xs={12} sm={6} md={4} key={set.id}>
                  <Paper sx={{ p: 2, cursor: 'pointer' }} onClick={() => setSelectedSet(set)}>
                    <Typography variant="h6">{set.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {yearGroups.find((yg) => yg.id === set.yearGroupId)?.name}
                    </Typography>
                    <Typography variant="body2">
                      {set.currentSize}/{set.capacity} students | {set.targetAbilityLevel} ability
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Year Group Dialog */}
        <Dialog
          open={dialogMode !== null && dialogType === 'yearGroup'}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{dialogMode === 'create' ? 'Add New Year Group' : 'Edit Year Group'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <YearGroupForm
                yearGroup={selectedYearGroup || undefined}
                onSubmit={handleYearGroupSubmit}
                onCancel={handleCloseDialog}
              />
            </Box>
          </DialogContent>
        </Dialog>

        {/* Class Dialog */}
        <Dialog
          open={dialogMode !== null && dialogType === 'class'}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{dialogMode === 'create' ? 'Add New Class' : 'Edit Class'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <ClassForm
                classData={selectedClass || undefined}
                yearGroups={yearGroups}
                onSubmit={handleClassSubmit}
                onCancel={handleCloseDialog}
              />
            </Box>
          </DialogContent>
        </Dialog>

        {/* Set Dialog */}
        <Dialog open={dialogMode !== null && dialogType === 'set'} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{dialogMode === 'create' ? 'Add New Set' : 'Edit Set'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <SetForm
                set={selectedSet || undefined}
                yearGroups={yearGroups}
                onSubmit={handleSetSubmit}
                onCancel={handleCloseDialog}
              />
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </Container>
  );
};

export default Students;
